<template>
  <div class="order-return-container" v-loading="loading">
    <!-- 顶部标题栏 -->
    <div class="page-header">
      <div class="header-title">
        <div class="title-mark"></div>
        <span>订单退货</span>
      </div>
      <div class="header-actions">
        <el-button @click="goBack">返回</el-button>
      </div>
    </div>

    <!-- 订单基本信息 -->
    <div class="info-section">
      <h3 class="section-title">订单信息</h3>
      <el-row :gutter="20">
        <el-col :span="8">
          <div class="info-item">
            <label>订单编号：</label>
            <span>{{ orderInfo.orderId }}</span>
          </div>
        </el-col>
        <el-col :span="8">
          <div class="info-item">
            <label>订单标题：</label>
            <span>{{ orderInfo.orderTitle }}</span>
          </div>
        </el-col>
        <el-col :span="8">
          <div class="info-item">
            <label>订单状态：</label>
            <el-tag :type="getStatusType(orderInfo.status)">
              {{ orderInfo.status | formatStatus }}
            </el-tag>
          </div>
        </el-col>
      </el-row>
      <el-row :gutter="20">
        <el-col :span="8">
          <div class="info-item">
            <label>供应商：</label>
            <span>{{ orderInfo.supplierName }}</span>
          </div>
        </el-col>
        <el-col :span="8">
          <div class="info-item">
            <label>创建时间：</label>
            <span>{{ orderInfo.createTime | formatDate }}</span>
          </div>
        </el-col>
        <el-col :span="8">
          <div class="info-item">
            <label>收货地址：</label>
            <span>{{ orderInfo.deliveryAddress }}</span>
          </div>
        </el-col>
      </el-row>
    </div>

    <!-- 已发货产品清单 -->
    <div class="info-section">
      <h3 class="section-title">已发货产品清单</h3>
      <p class="section-subtitle">请填写需要退货的数量和退货原因</p>
      
      <div v-if="shippedProducts.length === 0" class="no-data">
        <el-empty description="该订单暂无已发货产品"></el-empty>
      </div>
      
      <el-table
        v-else
        :data="shippedProducts"
        border
        style="width: 100%"
        :header-cell-style="{ background: '#f5f7fa', color: '#606266' }"
      >
        <el-table-column type="index" label="序号" width="60" align="center"></el-table-column>
        
        <el-table-column prop="productName" label="产品名称" min-width="150"></el-table-column>
        
        <el-table-column prop="specification" label="规格型号" width="150">
          <template slot-scope="scope">
            <el-tooltip
              v-if="scope.row.specification"
              :content="scope.row.specification"
              placement="top"
              effect="dark"
              popper-class="spec-tooltip"
            >
              <span>{{ truncateText(scope.row.specification, 20) }}</span>
            </el-tooltip>
            <span v-else>-</span>
          </template>
        </el-table-column>
        
        <el-table-column prop="unit" label="单位" width="80" align="center"></el-table-column>
        
        <el-table-column label="已发货数量" width="120" align="center">
          <template slot-scope="scope">
            <span class="shipped-quantity">{{ scope.row.shippedQuantity }}</span>
          </template>
        </el-table-column>
        
        <el-table-column label="已退货数量" width="120" align="center">
          <template slot-scope="scope">
            <span class="returned-quantity">{{ scope.row.returnedQuantity || 0 }}</span>
          </template>
        </el-table-column>
        
        <el-table-column label="可退货数量" width="120" align="center">
          <template slot-scope="scope">
            <span class="available-quantity">{{ scope.row.shippedQuantity - (scope.row.returnedQuantity || 0) }}</span>
          </template>
        </el-table-column>
        
        <el-table-column label="退货数量" width="150" align="center">
          <template slot-scope="scope">
            <el-input-number
              v-model="scope.row.returnQuantity"
              :min="0"
              :max="scope.row.shippedQuantity - (scope.row.returnedQuantity || 0)"
              :precision="4"
              :step="0.0001"
              size="small"
              style="width: 120px"
              @change="handleReturnQuantityChange(scope.row)"
            ></el-input-number>
          </template>
        </el-table-column>
        
        <el-table-column label="单价（含税）" width="120" align="center">
          <template slot-scope="scope">
            {{ formatUnitPrice(scope.row.taxInclusiveUnitPrice) }}
          </template>
        </el-table-column>
        
        <el-table-column label="退货金额" width="120" align="center">
          <template slot-scope="scope">
            <span class="return-amount">{{ formatAmount(scope.row.returnAmount) }}</span>
          </template>
        </el-table-column>
        
        <el-table-column label="退货原因" min-width="200">
          <template slot-scope="scope">
            <el-input
              v-model="scope.row.returnReason"
              placeholder="请输入退货原因"
              size="small"
              :disabled="!scope.row.returnQuantity || scope.row.returnQuantity === 0"
            ></el-input>
          </template>
        </el-table-column>
      </el-table>

      <!-- 退货汇总信息 -->
      <div class="return-summary" v-if="shippedProducts.length > 0">
        <el-row :gutter="20">
          <el-col :span="8">
            <div class="summary-item">
              <label>退货产品种类：</label>
              <span class="summary-value">{{ returnSummary.productCount }} 种</span>
            </div>
          </el-col>
          <el-col :span="8">
            <div class="summary-item">
              <label>退货总数量：</label>
              <span class="summary-value">{{ returnSummary.totalQuantity }} 件</span>
            </div>
          </el-col>
          <el-col :span="8">
            <div class="summary-item">
              <label>退货总金额：</label>
              <span class="summary-value highlight">{{ formatAmount(returnSummary.totalAmount) }} 元</span>
            </div>
          </el-col>
        </el-row>
      </div>
    </div>

    <!-- 底部操作按钮 -->
    <div class="footer-actions">
      <el-button @click="goBack">取消</el-button>
      <el-button v-permission="'orders:order-return:create'" type="primary" @click="submitReturn" :loading="submitLoading">提交退货申请</el-button>
    </div>
  </div>
</template>

<script>
import { get_order_detail } from '@/api/modules/orders.js';
import { get_order_shipments_with_details } from '@/api/modules/logistics.js';
import { create_return_order } from '@/api/modules/orders.js';

export default {
  name: 'OrderReturn',
  filters: {
    formatDate(dateString) {
      if (!dateString) return '';
      try {
        const date = new Date(dateString);
        if (isNaN(date.getTime())) return dateString;
        const year = date.getFullYear();
        const month = String(date.getMonth() + 1).padStart(2, '0');
        const day = String(date.getDate()).padStart(2, '0');
        return `${year}-${month}-${day}`;
      } catch (error) {
        return dateString;
      }
    },
    formatStatus(status) {
      const statusMap = {
        'ACTIVE': '待确认',
        'EXECUTING': '进行中',
        'SENDING': '已发货',
        'RECEIVING': '已收货',
        'END': '已结束'
      };
      return statusMap[status] || status;
    }
  },
  data() {
    return {
      orderId: null,
      submitLoading: false,
      loading: false,
      // 订单基本信息
      orderInfo: {},
      // 已发货产品清单
      shippedProducts: [],
      // 所有发货信息
      allShipments: [],
    };
  },
  computed: {
    // 退货汇总信息
    returnSummary() {
      const productCount = this.shippedProducts.filter(item => item.returnQuantity > 0).length;
      const totalQuantity = this.shippedProducts.reduce((sum, item) => sum + (item.returnQuantity || 0), 0);
      const totalAmount = this.shippedProducts.reduce((sum, item) => sum + (item.returnAmount || 0), 0);
      
      return {
        productCount,
        totalQuantity,
        totalAmount
      };
    }
  },
  created() {
    this.orderId = this.$route.params.orderId;
    this.loadOrderData();
  },
  methods: {
    // 加载订单数据
    async loadOrderData() {
      if (!this.orderId) {
        this.$message.error('缺少订单ID参数');
        this.goBack();
        return;
      }

      this.loading = true;
      
      try {
        // 并行获取订单详情和发货信息
        const [orderResponse, shipmentsResponse] = await Promise.all([
          get_order_detail(this.orderId),
          get_order_shipments_with_details(this.orderId)
        ]);

        // 处理订单详情
        if (orderResponse.data && orderResponse.data.success) {
          this.orderInfo = orderResponse.data.data;
} else {
          this.$message.error('获取订单详情失败');
          this.goBack();
          return;
        }

        // 处理发货信息
        if (shipmentsResponse.data && shipmentsResponse.data.success) {
          this.allShipments = shipmentsResponse.data.data;
          this.processShipmentData();
} else {
          this.$message.warning('该订单暂无发货信息');
          this.shippedProducts = [];
        }

      } catch (error) {
        console.error('加载订单数据失败:', error);
        this.$message.error('加载订单数据失败: ' + (error.message || '未知错误'));
        this.goBack();
      } finally {
        this.loading = false;
      }
    },

    // 处理发货数据，合并相同产品的发货数量
    processShipmentData() {
      const productMap = new Map();

      // 遍历所有发货批次
      this.allShipments.forEach(shipment => {
        if (shipment.shipmentItems && shipment.shipmentItems.length > 0) {
          shipment.shipmentItems.forEach(item => {
            const key = `${item.orderListId}_${item.productName}_${item.specification}`;
            
            if (productMap.has(key)) {
              // 累加已发货数量
              const existingItem = productMap.get(key);
              existingItem.shippedQuantity += item.shippedQuantity || 0;
            } else {
              // 创建新的产品项
              productMap.set(key, {
                id: item.orderListId,
                productName: item.productName,
                specification: item.specification,
                materialRequirement: item.materialRequirement || '',
                unit: item.unit || '个',
                shippedQuantity: item.shippedQuantity || 0,
                returnedQuantity: 0, // 暂时设为0，后续可以通过API获取
                taxInclusiveUnitPrice: item.taxInclusiveUnitPrice || 0,
                returnQuantity: 0,
                returnAmount: 0,
                returnReason: '',
                pricingMethod: item.pricingMethod || 'FIXED',
                taxRate: item.taxRate || 0,
                productSupplierId: item.productSupplierId,
                productSupplierName: item.productSupplierName || ''
              });
            }
          });
        }
      });

      // 转换为数组并排序
      this.shippedProducts = Array.from(productMap.values())
        .filter(item => item.shippedQuantity > 0) // 只显示已发货的产品
        .sort((a, b) => a.id - b.id);
      
},
    
    // 截断文本显示，默认为20个字符
    truncateText(text, length = 20) {
      if (!text) return '-';
      const str = String(text);
      return str.length > length ? `${str.slice(0, length)}...` : str;
    },

    // 格式化金额
    formatAmount(amount) {
      if (!amount && amount !== 0) return '0.00';
      const num = parseFloat(amount);
      if (isNaN(num)) return '0.00';
      return num.toLocaleString('zh-CN', {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2
      });
    },
    
    // 格式化单价（保留8位小数，去除末尾无意义的0）
    formatUnitPrice(amount) {
      if (!amount && amount !== 0) return '0';
      const num = parseFloat(amount);
      if (isNaN(num)) return '0';
      // 先格式化为8位小数，然后去除末尾的0
      const formatted = num.toFixed(8);
      // 去除末尾的0和小数点（如果有）
      return formatted.replace(/\.?0+$/, '') || '0';
    },
    
    // 获取状态标签类型
    getStatusType(status) {
      switch (status) {
        case 'ACTIVE':
          return 'warning';
        case 'EXECUTING':
          return 'success';
        case 'SENDING':
          return 'primary';
        case 'RECEIVING':
          return 'info';
        case 'END':
          return 'success';
        default:
          return 'info';
      }
    },
    
    // 处理退货数量变化
    handleReturnQuantityChange(row) {
      // 计算退货金额
      row.returnAmount = (row.returnQuantity || 0) * (row.taxInclusiveUnitPrice || 0);
      
      // 如果退货数量为0，清空退货原因
      if (!row.returnQuantity || row.returnQuantity === 0) {
        row.returnReason = '';
      }
    },
    
    // 提交退货申请
    async submitReturn() {
      // 验证是否有退货产品
      const hasReturnProducts = this.shippedProducts.some(item => item.returnQuantity > 0);
      if (!hasReturnProducts) {
        this.$message.warning('请至少选择一个产品进行退货');
        return;
      }
      
      // 验证退货产品是否填写了退货原因
      const productsWithoutReason = this.shippedProducts.filter(item => 
        item.returnQuantity > 0 && (!item.returnReason || item.returnReason.trim() === '')
      );
      
      if (productsWithoutReason.length > 0) {
        this.$message.warning('请为所有退货产品填写退货原因');
        return;
      }
      
      this.submitLoading = true;
      
      try {
        // 构建退货数据
        const returnItems = this.shippedProducts
          .filter(item => item.returnQuantity > 0)
          .map(item => ({
            originalOrderItemId: item.id,
            returnQuantity: item.returnQuantity,
            returnReason: item.returnReason.trim()
          }));

        const returnData = {
          originalOrderId: this.orderInfo.id,
          returnReason: `退货产品${returnItems.length}种，总数量${this.returnSummary.totalQuantity}件`,
          orderTitle: `${this.orderInfo.orderTitle || this.orderInfo.orderId || '订单'}退货申请`,
          returnItems: returnItems
        };
        
// 调用退货订单创建API
        const response = await create_return_order(returnData);
        
        if (response.data && response.data.success) {
          this.$message.success('退货申请提交成功');
          
          // 延迟返回列表页
          setTimeout(() => {
            this.goBack();
          }, 1500);
        } else {
          this.$message.error(response.data?.message || '退货申请提交失败');
        }
        
      } catch (error) {
        console.error('提交退货申请失败:', error);
        this.$message.error('退货申请提交失败: ' + (error.message || '未知错误'));
      } finally {
        this.submitLoading = false;
      }
    },
    
    // 返回上一页
    goBack() {
      this.$router.back();
    }
  }
};
</script>

<style scoped>
.order-return-container {
  padding: 20px;
  background-color: #f5f7fa;
  min-height: calc(100vh - 50px);
}

.page-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 20px;
  padding: 15px 20px;
  background-color: #fff;
  border-radius: 4px;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.05);
}

.header-title {
  display: flex;
  align-items: center;
}

.title-mark {
  width: 4px;
  height: 18px;
  background-color: #2c73eb;
  margin-right: 8px;
}

.header-title span {
  font-size: 18px;
  font-weight: 500;
  color: #303133;
}

.header-actions {
  display: flex;
  gap: 10px;
}

/* 信息区块样式 */
.info-section {
  background-color: #fff;
  padding: 20px;
  border-radius: 4px;
  margin-bottom: 20px;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
}

.section-title {
  font-size: 16px;
  font-weight: 500;
  color: #333;
  margin-bottom: 20px;
  padding-bottom: 10px;
  border-bottom: 1px solid #ebeef5;
}

.section-subtitle {
  font-size: 14px;
  color: #909399;
  margin-bottom: 20px;
  margin-top: -10px;
}

/* 信息项样式 */
.info-item {
  display: flex;
  align-items: center;
  margin-bottom: 15px;
  font-size: 14px;
}

.info-item label {
  font-weight: 500;
  color: #606266;
  margin-right: 10px;
  min-width: 120px;
  font-size: 14px;
}

.info-item span {
  color: #333;
  flex: 1;
  font-size: 14px;
}

/* 表格内数量样式 */
.shipped-quantity {
  color: #409eff;
  font-weight: 600;
}

.returned-quantity {
  color: #909399;
}

.available-quantity {
  color: #67c23a;
  font-weight: 600;
}

.return-amount {
  color: #f56c6c;
  font-weight: 600;
}

/* 退货汇总信息 */
.return-summary {
  margin-top: 20px;
  padding: 20px;
  background-color: #f8f9fa;
  border: 1px solid #e9ecef;
  border-radius: 4px;
}

.summary-item {
  display: flex;
  align-items: center;
  justify-content: center;
}

.summary-item label {
  font-size: 14px;
  color: #606266;
  margin-right: 8px;
}

.summary-value {
  font-size: 16px;
  font-weight: 600;
  color: #303133;
}

.summary-value.highlight {
  color: #f56c6c;
  font-size: 18px;
}

/* 底部操作按钮 */
.footer-actions {
  display: flex;
  justify-content: center;
  gap: 15px;
  padding: 20px;
  background-color: #fff;
  border-radius: 4px;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.05);
}

.footer-actions .el-button {
  min-width: 120px;
}


/* 空数据样式 */
.no-data {
  text-align: center;
  padding: 40px 0;
  color: #909399;
}

/* 响应式调整 */
@media (max-width: 768px) {
  .order-return-container {
    padding: 10px;
  }
  
  .page-header {
    flex-direction: column;
    align-items: flex-start;
    gap: 10px;
  }
  
  .return-summary .el-col {
    margin-bottom: 10px;
  }
}
</style>

<style lang="scss">
.spec-tooltip {
  max-width: 600px !important;
  white-space: normal;
  word-break: break-all;
}
</style>

